import { config } from '../config/index';

interface User {
  id: string;
  username: string;
  email: string;
  group: string;
  token: string;
}

class AuthService {
  private user: User | null = null;

  constructor() {
    this.loadUserFromStorage();
  }

  private loadUserFromStorage() {
    const storedUser = localStorage.getItem('kto_user');
    const token = localStorage.getItem('kto_token');
    
    if (storedUser && token) {
      this.user = JSON.parse(storedUser);
      this.user!.token = token;
    }
  }

  login(silent = false) {
    const { domain, userPoolClientId, redirectUri } = config.cognito;
    const promptParam = silent ? '&prompt=none' : '';
    const loginUrl = `${domain}/login?client_id=${userPoolClientId}&response_type=token&scope=openid+email+profile&redirect_uri=${encodeURIComponent(redirectUri)}${promptParam}`;
    window.location.href = loginUrl;
  }

  logout() {
    const { domain, userPoolClientId, logoutUri } = config.cognito;
    const logoutUrl = `${domain}/logout?client_id=${userPoolClientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
    
    localStorage.removeItem('kto_user');
    localStorage.removeItem('kto_token');
    this.user = null;
    
    window.location.href = logoutUrl;
  }

  handleCallback() {
    const hash = window.location.hash;
    if (!hash) return false;

    const params = new URLSearchParams(hash.substring(1));
    const idToken = params.get('id_token');
    const accessToken = params.get('access_token');

    if (idToken) {
      try {
        // Decode JWT token (without verification for now)
        const payload = this.parseJwt(idToken);
        
        this.user = {
          id: payload.sub,
          username: payload['cognito:username'] || payload.email,
          email: payload.email,
          group: payload['custom:group'] || payload['cognito:groups']?.[0] || 'unknown',
          token: idToken
        };

        localStorage.setItem('kto_user', JSON.stringify(this.user));
        localStorage.setItem('kto_token', idToken);
        
        // Clear hash from URL
        window.location.hash = '';
        
        return true;
      } catch (error) {
        console.error('Error parsing token:', error);
        return false;
      }
    }

    return false;
  }

  private parseJwt(token: string) {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  }

  isAuthenticated(): boolean {
    return this.user !== null && this.user.token !== null;
  }

  getUser(): User | null {
    return this.user;
  }

  getToken(): string | null {
    return this.user?.token || null;
  }

  getUserGroup(): string {
    return this.user?.group || 'unknown';
  }
}

export const authService = new AuthService();